/**
 * HRT (Health Record Type) Selection Helpers
 *
 * Utilities for extracting and resolving HRT data from events and context.
 */

import type { GetHrtsQuery } from "@frontend/lib/graphql/generated-gql";

export type HrtType = GetHrtsQuery["hrts"][number];

export interface HrtSelectionResult {
  selectedHrt: HrtType | undefined;
  selectedHrtId: number | undefined;
}

/**
 * Extracts the first HRT ID from event data.
 * Returns undefined if no valid HRT IDs are present.
 */
export function extractHrtIdFromEvent(
  eventData: { hrtIds?: number[] } | undefined,
): number | undefined {
  const hrtIds = eventData?.hrtIds;
  if (!hrtIds || hrtIds.length === 0) {
    return undefined;
  }
  return hrtIds[0];
}

/**
 * Finds an HRT object from the context's HRT list by ID.
 */
export function findHrtById(
  hrts: HrtType[] | undefined,
  hrtId: number | undefined,
): HrtType | undefined {
  if (hrtId === undefined || !hrts) {
    return undefined;
  }
  return hrts.find((hrt) => hrt.hrtId === hrtId);
}

/**
 * Resolves the selected HRT from event data.
 * Combines extractHrtIdFromEvent and findHrtById into a single operation.
 */
export function resolveSelectedHrt(
  eventData: { hrtIds?: number[] } | undefined,
  hrts: HrtType[] | undefined,
): HrtSelectionResult {
  const selectedHrtId = extractHrtIdFromEvent(eventData);
  const selectedHrt = findHrtById(hrts, selectedHrtId);
  return { selectedHrt, selectedHrtId };
}

/**
 * Gets the effective HRT from context (selectedHrt takes precedence over defaultHrt).
 * Consolidates the common pattern: context.selectedHrt || context.defaultHrt
 */
export function getEffectiveHrt(context: {
  selectedHrt?: HrtType | null;
  defaultHrt?: HrtType | null;
}): HrtType | undefined {
  return (context.selectedHrt ?? context.defaultHrt) as HrtType | undefined;
}

/**
 * Gets the effective HRT ID from context.
 */
export function getEffectiveHrtId(context: {
  selectedHrt?: { hrtId: number } | null;
  defaultHrt?: { hrtId: number } | null;
}): number | undefined {
  return context.selectedHrt?.hrtId ?? context.defaultHrt?.hrtId;
}
